import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Trash2, MapPin, ShieldCheck, ExternalLink, Flag, Check, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const ROLE_LABEL = { admin: "Admin", editor: "Editor", writer: "Scrittore" };

function DeleteButton({ label, onConfirm, testid }) {
    return (
        <AlertDialog>
            <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="text-destructive gap-1" data-testid={testid}>
                    <Trash2 size={15} /> Elimina
                </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Confermi l'eliminazione?</AlertDialogTitle>
                    <AlertDialogDescription>{label} Questa azione è irreversibile.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Annulla</AlertDialogCancel>
                    <AlertDialogAction onClick={onConfirm} className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

export default function Admin() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [editors, setEditors] = useState([]);
    const [users, setUsers] = useState([]);
    const [reports, setReports] = useState([]);

    const load = useCallback(async () => {
        const [e, u, r] = await Promise.all([
            api.get("/editors", { params: { sort: "reviews" } }),
            api.get("/admin/users"),
            api.get("/admin/reports"),
        ]);
        setEditors(e.data); setUsers(u.data); setReports(r.data);
    }, []);

    useEffect(() => {
        if (user === false || (user && user.role !== "admin")) { navigate("/"); return; }
        if (!user) return;
        load();
    }, [user, navigate, load]);

    const toggleVerify = async (ed, value) => {
        try {
            await api.put(`/admin/editors/${ed.editor_id}/verify`, { verified: value });
            setEditors((list) => list.map((x) => (x.editor_id === ed.editor_id ? { ...x, verified: value } : x)));
            toast.success(value ? "Editor verificato" : "Verifica rimossa");
        } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail)); }
    };

    const deleteEditor = async (ed) => {
        try {
            await api.delete(`/admin/editors/${ed.editor_id}`);
            setEditors((list) => list.filter((x) => x.editor_id !== ed.editor_id));
            toast.success("Profilo editor eliminato");
        } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail)); }
    };

    const deleteUser = async (u) => {
        try {
            await api.delete(`/admin/users/${u.user_id}`);
            setUsers((list) => list.filter((x) => x.user_id !== u.user_id));
            toast.success("Utente eliminato");
            load();
        } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail)); }
    };

    const setReportStatus = async (rep, status) => {
        try {
            await api.put(`/admin/reports/${rep.report_id}/status`, { status });
            setReports((list) => list.map((x) => (x.report_id === rep.report_id ? { ...x, status } : x)));
        } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail)); }
    };

    const rejectVerification = async (ed) => {
        try {
            await api.put(`/admin/editors/${ed.editor_id}/reject-verification`);
            setEditors((list) => list.map((x) => (x.editor_id === ed.editor_id ? { ...x, verified: false, verification_status: "rejected" } : x)));
            toast.success("Richiesta di verifica rifiutata");
        } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail)); }
    };

    const pendingEditors = editors.filter((e) => !e.verified && e.verification_status === "pending");

    if (!user) return <div className="py-24 text-center text-muted-foreground">Caricamento…</div>;

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="flex items-center gap-3 mb-2">
                <Shield className="text-primary" size={28} />
                <h1 className="text-4xl font-serif font-medium">Pannello amministratore</h1>
            </div>
            <p className="text-muted-foreground mb-8">Verifica gli editor affidabili ed elimina profili o account non corretti.</p>

            <Tabs defaultValue="editors">
                <TabsList className="mb-6">
                    <TabsTrigger value="editors" data-testid="admin-tab-editors">Editor ({editors.length})</TabsTrigger>
                    <TabsTrigger value="pending" data-testid="admin-tab-pending" className="gap-1"><ShieldCheck size={14} /> In attesa ({pendingEditors.length})</TabsTrigger>
                    <TabsTrigger value="users" data-testid="admin-tab-users">Utenti ({users.length})</TabsTrigger>
                    <TabsTrigger value="reports" data-testid="admin-tab-reports" className="gap-1"><Flag size={14} /> Segnalazioni ({reports.filter((r) => r.status === "open").length})</TabsTrigger>
                </TabsList>

                <TabsContent value="editors" className="space-y-3">
                    {editors.map((ed) => (
                        <div key={ed.editor_id} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`admin-editor-${ed.editor_id}`}>
                            <div className="flex items-center gap-3 min-w-0">
                                {ed.photo_url
                                    ? <img src={ed.photo_url} alt="" className="w-12 h-12 rounded-full object-cover shrink-0" />
                                    : <div className="w-12 h-12 rounded-full bg-secondary shrink-0" />}
                                <div className="min-w-0">
                                    <p className="font-semibold flex items-center gap-1.5 truncate">{ed.name} {ed.verified && <VerifiedBadge size={15} />}{!ed.verified && ed.verification_status === "pending" && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gold/20 text-gold" data-testid={`pending-verif-${ed.editor_id}`}>verifica richiesta</span>}</p>
                                    <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin size={13} /> {ed.location}, {ed.region}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <ShieldCheck size={16} className="text-sage" />
                                    <Switch checked={!!ed.verified} onCheckedChange={(v) => toggleVerify(ed, v)} data-testid={`verify-switch-${ed.editor_id}`} />
                                    Verificato
                                </label>
                                <Link to={`/editors/${ed.editor_id}`} className="text-muted-foreground hover:text-primary"><ExternalLink size={16} /></Link>
                                <DeleteButton label={`Il profilo di ${ed.name} verrà rimosso con recensioni e richieste.`} onConfirm={() => deleteEditor(ed)} testid={`delete-editor-${ed.editor_id}`} />
                            </div>
                        </div>
                    ))}
                </TabsContent>

                <TabsContent value="pending" className="space-y-3">
                    {pendingEditors.length === 0 && <p className="text-muted-foreground py-8 text-center">Nessuna richiesta di verifica in sospeso.</p>}
                    {pendingEditors.map((ed) => (
                        <div key={ed.editor_id} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`pending-editor-${ed.editor_id}`}>
                            <div className="flex items-center gap-3 min-w-0">
                                {ed.photo_url
                                    ? <img src={ed.photo_url} alt="" className="w-12 h-12 rounded-full object-cover shrink-0" />
                                    : <div className="w-12 h-12 rounded-full bg-secondary shrink-0" />}
                                <div className="min-w-0">
                                    <p className="font-semibold truncate">{ed.name}</p>
                                    <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin size={13} /> {ed.location}, {ed.region}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Link to={`/editors/${ed.editor_id}`} className="text-muted-foreground hover:text-primary"><ExternalLink size={16} /></Link>
                                <Button size="sm" variant="outline" onClick={() => rejectVerification(ed)} className="text-destructive gap-1" data-testid={`reject-verif-${ed.editor_id}`}><X size={14} /> Rifiuta</Button>
                                <Button size="sm" onClick={() => toggleVerify(ed, true)} className="bg-primary gap-1" data-testid={`approve-verif-${ed.editor_id}`}><Check size={14} /> Approva</Button>
                            </div>
                        </div>
                    ))}
                </TabsContent>

                <TabsContent value="users" className="space-y-3">
                    {users.map((u) => (
                        <div key={u.user_id} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`admin-user-${u.user_id}`}>
                            <div className="min-w-0">
                                <p className="font-semibold truncate">{u.name || "—"}</p>
                                <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                            </div>
                            <div className="flex items-center gap-4">
                                <span className="text-xs px-2.5 py-1 rounded-full bg-secondary">{ROLE_LABEL[u.role] || u.role}</span>
                                {u.role === "admin" ? (
                                    <span className="text-xs text-muted-foreground">Protetto</span>
                                ) : (
                                    <DeleteButton label={`L'account di ${u.name || u.email} verrà eliminato con eventuale profilo editor e recensioni.`} onConfirm={() => deleteUser(u)} testid={`delete-user-${u.user_id}`} />
                                )}
                            </div>
                        </div>
                    ))}
                </TabsContent>

                <TabsContent value="reports" className="space-y-3">
                    {reports.length === 0 && <p className="text-muted-foreground py-8 text-center">Nessuna segnalazione ricevuta.</p>}
                    {reports.map((rep) => (
                        <div key={rep.report_id} className="bg-card border border-border rounded-lg p-4" data-testid={`admin-report-${rep.report_id}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0">
                                    <p className="font-semibold flex items-center gap-1.5"><Flag size={15} className="text-destructive" /> {rep.editor_name}</p>
                                    <p className="text-sm mt-1"><span className="font-medium">Motivo:</span> {rep.reason}</p>
                                    {rep.details && <p className="text-sm text-muted-foreground mt-0.5">{rep.details}</p>}
                                    <p className="text-xs text-muted-foreground mt-1">Segnalato da {rep.reporter_name} ({rep.reporter_email})</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Link to={`/editors/${rep.editor_id}`} className="text-muted-foreground hover:text-primary"><ExternalLink size={16} /></Link>
                                    <Select value={rep.status} onValueChange={(v) => setReportStatus(rep, v)}>
                                        <SelectTrigger className="w-[150px]" data-testid={`report-status-${rep.report_id}`}><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="open">Aperta</SelectItem>
                                            <SelectItem value="reviewed">Esaminata</SelectItem>
                                            <SelectItem value="dismissed">Archiviata</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    ))}
                </TabsContent>
            </Tabs>
        </div>
    );
}
